import React, { useState, useEffect } from 'react';
import { FiZap } from "react-icons/fi";
import { motion } from "framer-motion";
import { SiGithub } from "react-icons/si";
import { FiStar } from "react-icons/fi";
import Highlight, { Prism } from 'prism-react-renderer';

// Import Prism languages
(typeof global !== 'undefined' ? global : window).Prism = Prism;
require('prismjs/components/prism-php');
require('prismjs/components/prism-sql');

// Modern theme with vibrant colors
const modernTheme = {
  plain: {
    color: '#F8F8F2',
    backgroundColor: '#0F1419',
  },
  styles: [
    {
      types: ['comment', 'prolog', 'doctype', 'cdata'],
      style: {
        color: '#7A8A9B',
        fontStyle: 'italic',
      },
    },
    {
      types: ['namespace'],
      style: {
        opacity: 0.7,
      },
    },
    {
      types: ['string', 'attr-value'],
      style: {
        color: '#4FDD8E',
      },
    },
    {
      types: ['punctuation', 'operator'],
      style: {
        color: '#00D9FF',
      },
    },
    {
      types: ['entity', 'url', 'symbol', 'number', 'boolean', 'variable', 'constant', 'property', 'regex', 'inserted'],
      style: {
        color: '#FFB454',
      },
    },
    {
      types: ['atrule', 'keyword', 'attr-name', 'selector'],
      style: {
        color: '#5A9EFF',
      },
    },
    {
      types: ['function', 'deleted', 'tag'],
      style: {
        color: '#1BDCAF',
      },
    },
    {
      types: ['function-variable'],
      style: {
        color: '#00D9FF',
      },
    },
    {
      types: ['tag', 'selector', 'keyword'],
      style: {
        color: '#00BFFF',
      },
    },
  ],
};

export const CodeBeams = () => {
  return (
    <div className="relative overflow-hidden bg-darkBlue text-slate-200">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <section className="relative z-20 py-20 md:py-36">
          <span className="mx-auto mb-3 block w-fit rounded bg-gradient-to-br from-slate-800 to-slate-950 p-3 text-3xl shadow-md shadow-indigo-900">
            <FiZap />
          </span>
          <h2 className="mb-3 text-center text-3xl font-semibold leading-tight sm:text-4xl">
            Your Ideas, Expertly Coded
          </h2>
          <p className="mb-6 text-center text-base leading-snug text-slate-400 sm:text-lg sm:leading-snug md:text-xl md:leading-snug">
            From front-end to back-end, I use various languages to make your vision a reality.       
          </p>
          <CodeCard />
        </section>
      </div>
      <BGGrid />
    </div>
  );
};

const BGGrid = () => {
  return (
    <div
      style={{
        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' width='32' height='32' fill='none' stroke-width='2' stroke='rgb(30 27 75 / 0.5)'%3e%3cpath d='M0 .5H31.5V32'/%3e%3c/svg%3e")`,
      }}
      className="absolute bottom-0 left-0 right-0 top-0"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/0 to-slate-950/80" />
      <Beams />
    </div>
  );
};

const Beams = () => {
  const { width } = useWindowSize();

  const numColumns = width ? Math.floor(width / GRID_BOX_SIZE) : 0;

  const placements = [
    {
      top: GRID_BOX_SIZE * 0,
      left: Math.floor(numColumns * 0.05) * GRID_BOX_SIZE,
      transition: {
        duration: 3.5,
        repeatDelay: 5,
        delay: 2,
      },
    },
    {
      top: GRID_BOX_SIZE * 12,
      left: Math.floor(numColumns * 0.15) * GRID_BOX_SIZE,
      transition: {
        duration: 3.5,
        repeatDelay: 10,
        delay: 4,
      },
    },
    {
      top: GRID_BOX_SIZE * 3,
      left: Math.floor(numColumns * 0.25) * GRID_BOX_SIZE,
    },
    {
      top: GRID_BOX_SIZE * 9,
      left: Math.floor(numColumns * 0.75) * GRID_BOX_SIZE,
      transition: {
        duration: 2,
        repeatDelay: 7.5,
        delay: 3.5,
      },
    },
    {
      top: 0,
      left: Math.floor(numColumns * 0.7) * GRID_BOX_SIZE,
      transition: {
        duration: 3,
        repeatDelay: 2,
        delay: 1,
      },
    },
    {
      top: GRID_BOX_SIZE * 2,
      left: Math.floor(numColumns * 1) * GRID_BOX_SIZE - GRID_BOX_SIZE,
      transition: {
        duration: 5,
        repeatDelay: 5,
        delay: 5,
      },
    },
  ];

  return (
    <>
      {placements.map((p, i) => (
        <Beam
          key={i}
          top={p.top}
          left={p.left - BEAM_WIDTH_OFFSET}
          transition={p.transition || {}}
        />
      ))}
    </>
  );
};

const Beam = ({ top, left, transition = {} }) => {
  return (
    <motion.div
      initial={{
        y: 0,
        opacity: 0,
      }}
      animate={{
        opacity: [0, 1, 0],
        y: 32 * 8,
      }}
      transition={{
        ease: "easeInOut",
        duration: 3,
        repeat: Infinity,
        repeatDelay: 1.5,
        ...transition,
      }}
      style={{
        top,
        left,
      }}
      className="absolute z-10 h-[64px] w-[1px] bg-gradient-to-b from-indigo-500/0 to-indigo-500"
    />
  );
};

const CodeCard = () => {
  const [selected, setSelected] = useState("php");

  return (
    <Card className="mx-auto max-w-3xl pt-3">
      <div className="-mx-6 mb-6 flex items-center justify-between border-b border-slate-700 px-6 pb-3">
        <div className="flex items-center gap-3">
          <ToggleChip
            onClick={() => setSelected("php")}
            selected={selected === "php"}
          >
            PHP
          </ToggleChip>
          <ToggleChip
            onClick={() => setSelected("js")}
            selected={selected === "js"}
          >
            JavaScript
          </ToggleChip>
          <ToggleChip
            onClick={() => setSelected("sql")}
            selected={selected === "sql"}
          >
            SQL
          </ToggleChip>
          <ToggleChip
            onClick={() => setSelected("html")}
            selected={selected === "html"}
          >
            HTML
          </ToggleChip>
          <ToggleChip
            onClick={() => setSelected("css")}
            selected={selected === "css"}
          >
            CSS
          </ToggleChip>
        </div>
        <button
          className={`
                relative z-0 flex items-center gap-2 overflow-hidden whitespace-nowrap rounded-md 
                bg-gradient-to-br from-slate-800 to-slate-950 px-3 py-1.5
                text-xs font-medium text-slate-50
                transition-all duration-300
                
                before:absolute before:inset-0
                before:-z-10 before:translate-y-[200%]
                before:scale-[2.5]
                before:rounded-[100%] before:bg-slate-100
                before:transition-transform before:duration-500
                before:content-[""]
        
                hover:scale-105 hover:text-slate-900
                hover:before:translate-y-[0%]
                active:scale-100`}
        >
          <SiGithub />
          <FiStar className="hidden sm:inline" />
          <span className="hidden sm:inline">1337</span>
        </button>
      </div>
      <div className="-mx-6 overflow-x-auto px-6">
        <Markup
          code={
            selected === "php"
              ? phpCode
              : selected === "js"
              ? javascriptCode
              : selected === "sql"
              ? sqlCode
              : selected === "html"
              ? htmlCode
              : cssCode
          }
          language={
            selected === "php"
              ? "php"
              : selected === "js"
              ? "javascript"
              : selected === "sql"
              ? "sql"
              : selected === "html"
              ? "markup"
              : "css"
          }
        />
      </div>
      <span className="absolute left-0 top-1/2 h-48 w-[1px] -translate-y-1/2 animate-pulse bg-gradient-to-b from-indigo-500/0 via-indigo-800 to-indigo-500/0" />
    </Card>
  );
};

const ToggleChip = ({ children, selected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`rounded px-1.5 py-0.5 text-sm font-medium transition-colors ${
        selected ? "bg-indigo-600 text-slate-50" : "bg-slate-900 text-slate-50 hover:bg-slate-700"
      }`}
    >
      {children}
    </button>
  );
};

const Card = ({ className, children }) => {
  return (
    <motion.div
      initial={{
        filter: "blur(4px)",
      }}
      whileInView={{
        filter: "blur(0px)",
      }}
      transition={{
        duration: 0.5,
        ease: "easeInOut",
        delay: 0.25,
      }}
      className={`relative h-full w-full overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/50 to-slate-900/80 p-6 ${className}`}
    >
      {children}
    </motion.div>
  );
};

const Markup = ({ code, language }) => {
  return (
    <Highlight Prism={Prism} theme={modernTheme} code={code} language={language}>
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <div style={{ position: 'relative' }}>
          <pre
            className={className}
            style={{
              ...style,
              padding: "1.25rem",
              paddingLeft: "3.5rem",
              overflowX: "auto",
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              whiteSpace: "pre",
              fontSize: "0.813rem",
              lineHeight: "1.5",
              borderRadius: "0.5rem",
              fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', 'Monaco', monospace",
            }}
          >
            <style>
              {`
                pre::-webkit-scrollbar {
                  display: none;
                }
              `}
            </style>
            {tokens.map((line, i) => {
              const { key, ...restLineProps } = getLineProps({ line, key: i });
              return (
                <div
                  key={key}
                  {...restLineProps}
                  style={{
                    ...restLineProps.style,
                    position: 'relative',
                    minHeight: '1.5rem',
                  }}
                >
                  <span
                    className="select-none opacity-40"
                    style={{
                      position: 'absolute',
                      left: '-3.5rem',
                      width: '2.5rem',
                      textAlign: 'right',
                      paddingRight: '1rem',
                      userSelect: 'none',
                    }}
                  >
                    {i + 1}
                  </span>
                  <span>
                    {line.map((token, key) => {
                      const { key: tokenKey, ...restTokenProps } = getTokenProps({ token, key });
                      return <span key={tokenKey} {...restTokenProps} />;
                    })}
                  </span>
                </div>
              );
            })}
          </pre>
        </div>
      )}
    </Highlight>
  );
};

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return windowSize;
};

const GRID_BOX_SIZE = 32;
const BEAM_WIDTH_OFFSET = 1;

// HTML Code Example with Tailwind
const htmlCode = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Modern Portfolio with Tailwind</title>
  <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">

  <div class="max-w-md w-full">
    <!-- Card with glass morphism effect -->
    <div class="backdrop-blur-lg bg-white/10 rounded-2xl shadow-2xl border border-white/20 p-8 space-y-6">

      <!-- Avatar -->
      <div class="flex justify-center">
        <div class="w-24 h-24 rounded-full bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center text-white text-4xl font-bold shadow-lg">
          R
        </div>
      </div>

      <!-- Content -->
      <div class="text-center space-y-4">
        <h1 class="text-3xl font-bold text-white">Hello, I'm Rasmus! 👋</h1>
        <p class="text-gray-300 text-lg">
          Crafting digital experiences with modern web technologies
        </p>
      </div>

      <!-- Action Button with hover effect -->
      <button id="ctaButton"
              class="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
        Let's Build Something Amazing
      </button>

      <!-- Tech Stack Tags -->
      <div class="flex flex-wrap gap-2 justify-center pt-4">
        <span class="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm border border-blue-500/30">React</span>
        <span class="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm border border-green-500/30">Node.js</span>
        <span class="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm border border-purple-500/30">Tailwind</span>
      </div>
    </div>
  </div>

  <script>
    document.getElementById('ctaButton').addEventListener('click', () => {
      alert('Ready to transform your ideas into reality! 🚀');
    });
  </script>
</body>
</html>`;

// CSS Code Example - Modern CSS with comparisons
const cssCode = `/* Modern CSS with Custom Properties & Advanced Features
   I work with vanilla CSS, Sass/SCSS, LESS, and Tailwind */

:root {
  /* Design tokens - easily themeable */
  --color-primary: #8b5cf6;
  --color-secondary: #3b82f6;
  --color-accent: #10b981;
  --spacing-unit: 0.25rem;
  --radius-lg: 1rem;
  --shadow-soft: 0 10px 40px rgba(0, 0, 0, 0.1);
  --transition-smooth: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Container Queries - Modern responsive design */
.card-container {
  container-type: inline-size;
  container-name: card;
}

.dashboard-card {
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  padding: calc(var(--spacing-unit) * 6);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(10px);
  transition: var(--transition-smooth);
}

.dashboard-card:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 20px 60px rgba(139, 92, 246, 0.3);
}

/* Container query - card adapts to container size, not viewport */
@container card (min-width: 500px) {
  .dashboard-card {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: calc(var(--spacing-unit) * 4);
  }
}

/* Modern CSS Nesting (native in 2024+) */
.button {
  background: var(--color-accent);
  color: white;
  padding: calc(var(--spacing-unit) * 3) calc(var(--spacing-unit) * 6);
  border: none;
  border-radius: calc(var(--radius-lg) / 2);
  cursor: pointer;
  transition: var(--transition-smooth);

  &:hover {
    background: color-mix(in srgb, var(--color-accent) 80%, black);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
}

/* ============================================
   SASS/SCSS VERSION - Even more powerful!
   ============================================ */

// SCSS Variables & Maps
$theme-colors: (
  'primary': #8b5cf6,
  'secondary': #3b82f6,
  'accent': #10b981
);

// SCSS Mixins - DRY code
@mixin flex-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

@mixin responsive-text($mobile, $desktop) {
  font-size: $mobile;

  @media (min-width: 768px) {
    font-size: $desktop;
  }
}

.hero-section {
  @include flex-center;
  min-height: 100vh;

  .hero-title {
    @include responsive-text(2rem, 3.5rem);
    background: linear-gradient(
      to right,
      map-get($theme-colors, 'primary'),
      map-get($theme-colors, 'secondary')
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
}

/* ============================================
   TAILWIND EQUIVALENT
   ============================================
   <div class="flex justify-center items-center min-h-screen">
     <h1 class="text-4xl md:text-6xl bg-gradient-to-r from-purple-500 to-blue-500 bg-clip-text text-transparent">
       Modern Design
     </h1>
   </div>
*/`;

// JavaScript Code Example
const javascriptCode = `// Simple JavaScript example
document.addEventListener('DOMContentLoaded', () => {
  console.log('Hello from JavaScript! 🚀');
  
  // Checking for jQuery, because sometimes old friends stick around
  if (typeof $ !== 'undefined') {
    console.log("Hello from jQuery! 🕰️ Still kicking it like it's 2010!");
    $('#myElement').text("jQuery is here, doing its thing!");
  }
  
  // And if React has entered the chat...
  if (typeof React !== 'undefined') {
    console.log("React says 👋");
    const MyComponent = () => <div>Hello from React! ⚛️</div>;
    console.log(MyComponent().props.children);
  }
});`;

// PHP Code Example
const phpCode = `<?php
/**
 * Registers a secure REST API endpoint for contact form submissions.
 * Handles validation, sanitization, and simple rate limiting.
 */
add_action( 'rest_api_init', function () {
    register_rest_route( 'plambech/v1', '/contact', [
        'methods'             => WP_REST_Server::CREATABLE,
        'callback'            => 'handle_contact_submission',
        'permission_callback' => '__return_true',
        'args'                => [
            'name'    => [
                'required'          => true,
                'sanitize_callback' => 'sanitize_text_field',
            ],
            'email'   => [
                'required'          => true,
                'sanitize_callback' => 'sanitize_email',
                'validate_callback' => 'is_email',
            ],
            'message' => [
                'required'          => true,
                'sanitize_callback' => 'sanitize_textarea_field',
            ],
        ],
    ] );
} );

function handle_contact_submission( WP_REST_Request $request ): WP_REST_Response {
    $name    = $request->get_param( 'name' );
    $email   = $request->get_param( 'email' );
    $message = $request->get_param( 'message' );

    // Rate limiting via transients — one submission per IP per 10 minutes
    $ip_key = 'contact_rate_' . md5( $_SERVER['REMOTE_ADDR'] );
    if ( get_transient( $ip_key ) ) {
        return new WP_REST_Response(
            [ 'error' => 'Please wait before submitting again.' ],
            429
        );
    }
    set_transient( $ip_key, true, 10 * MINUTE_IN_SECONDS );

    $sent = wp_mail(
        get_option( 'admin_email' ),
        "New message from {$name}",
        "From: {$email}\\n\\n{$message}",
        [ 'Content-Type: text/plain; charset=UTF-8' ]
    );

    return new WP_REST_Response(
        [ 'success' => $sent ],
        $sent ? 200 : 500
    );
}`;

// SQL Code Example
const sqlCode = `-- Simple SQL example
-- Let's mimic WordPress's database structure 🛠️

CREATE DATABASE wordpress_db;

USE wordpress_db;

-- Creating a simplified version of the wp_posts table
CREATE TABLE wp_posts (
  ID BIGINT UNSIGNED AUTO_INCREMENT PRIMARY KEY,
  post_author BIGINT UNSIGNED NOT NULL DEFAULT '0',
  post_date DATETIME NOT NULL DEFAULT CURRENT_TIMESTAMP,
  post_content LONGTEXT NOT NULL,
  post_title TEXT NOT NULL,
  post_status VARCHAR(20) NOT NULL DEFAULT 'publish'
);

-- Inserting a post like WordPress does
INSERT INTO wp_posts (post_author, post_content, post_title, post_status) VALUES
(1, 'Hello World! This is our first post. 🌍', 'Hello World', 'publish');

-- Fetching posts
SELECT * FROM wp_posts;

-- WordPress loves its SQL queries! ⚙️`;

export default CodeBeams;